import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import DropDown from "../Elements/Dropdown";
import useUserLocation from "../../hooks/useUserLocation";

import {
  currentCity,
  setManualSelection,
  setSelected,
  userCurrentCity,
} from "../../redux/reducers/citySelectorSlice";
import { resolveAvailableCity } from "../../utils/city";

// Predefined coordinates for cities
const cityCoordinates = {
  Mumbai: { latitude: 19.076, longitude: 72.8777 },
  Thane: { latitude: 19.197, longitude: 72.9635 },
  Delhi: { latitude: 28.7041, longitude: 77.1025 },
  "Delhi NCR": { latitude: 28.7041, longitude: 77.1025 },
  "New Delhi": { latitude: 28.6139, longitude: 77.209 },
  Bangalore: { latitude: 12.9716, longitude: 77.5946 },
  Bengaluru: { latitude: 12.9716, longitude: 77.5946 },
  Hyderabad: { latitude: 17.385, longitude: 78.4867 },
  Chennai: { latitude: 13.0827, longitude: 80.2707 },
  Kolkata: { latitude: 22.5726, longitude: 88.3639 },
  Pune: { latitude: 18.5204, longitude: 73.8567 },
  Ahmedabad: { latitude: 23.0225, longitude: 72.5714 },
  Surat: { latitude: 21.1702, longitude: 72.8311 },
  Goa: { latitude: 15.2993, longitude: 74.124 },
  Chandigarh: { latitude: 30.7333, longitude: 76.7794 },
  Dehradun: { latitude: 30.3165, longitude: 78.0322 },
  Guwahati: { latitude: 26.1445, longitude: 91.7362 },
  Indore: { latitude: 22.7196, longitude: 75.8577 },
  Jaipur: { latitude: 26.9124, longitude: 75.7873 },
  Kochi: { latitude: 9.9312, longitude: 76.2673 },
};

const CityDropDown = ({
  isSelected = () => { },
  onPress = () => { },
  shouldUpdateRedux = true,
}) => {
  const dispatch = useDispatch();

  const selectedCity = useSelector(
    (state) => state.citySelector.selectedCity
  );

  const isManualSelection = useSelector(
    (state) => state.citySelector.isManualSelection
  );

  const [localCity, setLocalCity] = useState("");
  const [hasManualSelection, setHasManualSelection] =
    useState(false);

  const {
    locationData,
    nearestTrainStation,
    coords,
  } = useUserLocation();

  const fetchClubs = async () => {
    try {
      const params = {
        q: "near me clubs",
        city: selectedCity,
        locality: locationData?.locality || "",
        trainStation: nearestTrainStation || "",
        userBaseCity: selectedCity || "",
        location: JSON.stringify({
          coordinates: [
            selectedCity && cityCoordinates[selectedCity]
              ? cityCoordinates[selectedCity].longitude
              : coords?.longitude,
            selectedCity && cityCoordinates[selectedCity]
              ? cityCoordinates[selectedCity].latitude
              : coords?.latitude,
          ],
          locality: locationData?.locality || "",
          city: selectedCity || "",
          trainStation: nearestTrainStation || "",
        }),
      };

      // Reserved for AI near-me clubs lookup when endpoint is available.
      void params;
    } catch (error) {
      console.log("[CityDropDown] API Error:", error);
    }
  };

  const { data } = useQuery({
    queryKey: ["api/cities"],
    placeholderData: [],
  });

  const detectedCity = useMemo(() => {
    return locationData?.city?.trim() || "";
  }, [locationData?.city]);

  const cityItems = useMemo(() => {
    return Array.isArray(data?.data) ? data.data : [];
  }, [data]);

  useEffect(() => {
    if (
      !detectedCity ||
      hasManualSelection ||
      isManualSelection
    )
      return;

    const matchedCity = resolveAvailableCity(
      detectedCity,
      cityItems
    );

    if (!matchedCity) return;

    dispatch(
      currentCity(
        matchedCity.name
      )
    );

    dispatch(
      userCurrentCity(
        matchedCity.name
      )
    );

    dispatch(setSelected(true));
    dispatch(setManualSelection(false));

    isSelected(true);
    onPress(matchedCity.name);

    fetchClubs();
  }, [
    detectedCity,
    cityItems,
    hasManualSelection,
    isManualSelection,
  ]);

  useEffect(() => {
    const valid =
      cityItems.some(
        (item) =>
          item?.name ===
          selectedCity
      );

    dispatch(setSelected(valid));
    isSelected(valid);
  }, [
    cityItems,
    selectedCity,
  ]);

  useEffect(() => {
    if (!shouldUpdateRedux) {
      setLocalCity(
        selectedCity || ""
      );

      onPress(selectedCity);
    }
  }, [
    selectedCity,
    shouldUpdateRedux,
  ]);

  const handleSelect = (
    city
  ) => {
    setHasManualSelection(true);

    dispatch(currentCity(city));

    dispatch(
      userCurrentCity(city)
    );

    dispatch(setSelected(true));
    dispatch(setManualSelection(true));

    isSelected(city);
    onPress(city);

    fetchClubs();
  };

  return (
    <DropDown
      items={cityItems}
      value={
        shouldUpdateRedux
          ? selectedCity || ""
          : localCity
      }
      onPress={handleSelect}
    />
  );
};

export default CityDropDown;
